import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { Lock, CheckCircle2, Play, Trophy, Star, ChevronDown, Award, Zap, Target, Sparkles } from "lucide-react";

const LEVEL_TINTS = ["#0D9488", "#0284C7", "#7A3FF2", "#E05A47", "#D97706", "#10B981", "#DB2777", "#F59E0B"];

function StatusBadge({ status }) {
  const map = {
    completed: ["Completed", "bg-[#10B981]/15 text-[#047857]", CheckCircle2],
    in_progress: ["In Progress", "bg-[#F59E0B]/15 text-[#B45309]", Play],
    available: ["Available", "bg-[#0284C7]/15 text-[#0369A1]", Play],
    locked: ["Locked", "bg-[#94A3B8]/20 text-[#64748B]", Lock],
    coming_soon: ["Coming Soon", "bg-[#94A3B8]/20 text-[#64748B]", Sparkles],
  };
  const [label, cls, Icon] = map[status] || map.locked;
  return <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${cls}`} data-testid={`level-status`}><Icon size={13} /> {label}</span>;
}

export default function CurriculumMap() {
  const { user, overlay, refreshCurriculum } = useApp();
  const navigate = useNavigate();
  useEffect(() => { refreshCurriculum && refreshCurriculum(); }, [refreshCurriculum]);

  if (!overlay) return <div className="p-16 text-center font-cursive text-2xl text-[#0D9488] animate-pulse">Loading your journey…</div>;

  const cont = overlay.continue_activity;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
      <div className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">Cursive Missions</div>
      <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1F2937]">Hi {user?.name}! 👋</h1>

      {/* Dashboard summary */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-3xl border border-[#E5DECF] bg-white p-5">
          <div className="flex items-center gap-2 text-[#0D9488]"><Target size={18} /><span className="text-xs uppercase tracking-wide font-semibold text-[#6B7280]">Overall Progress</span></div>
          <div className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]" data-testid="overall-progress">{overlay.overall_progress_percentage ?? overlay.overall_percentage}%</div>
        </div>
        <div className="rounded-3xl border border-[#E5DECF] bg-white p-5">
          <div className="flex items-center gap-2 text-[#10B981]"><CheckCircle2 size={18} /><span className="text-xs uppercase tracking-wide font-semibold text-[#6B7280]">Skills Mastered</span></div>
          <div className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]" data-testid="skills-mastered">{overlay.skills_mastered}</div>
        </div>
        <div className="rounded-3xl border border-[#E5DECF] bg-white p-5">
          <div className="flex items-center gap-2 text-[#F59E0B]"><Zap size={18} /><span className="text-xs uppercase tracking-wide font-semibold text-[#6B7280]">Points</span></div>
          <div className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]" data-testid="points">{overlay.points}</div>
        </div>
        <div className="rounded-3xl border border-[#E5DECF] bg-white p-5">
          <div className="flex items-center gap-2 text-[#7A3FF2]"><Trophy size={18} /><span className="text-xs uppercase tracking-wide font-semibold text-[#6B7280]">Current Level</span></div>
          <div className="mt-1 font-heading text-lg font-extrabold text-[#1F2937]">L{overlay.current_level?.level_number} · {overlay.current_level?.title}</div>
        </div>
      </div>

      {/* Continue Learning */}
      {cont && (
        <motion.button
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          onClick={() => navigate(`/activity/${cont.activity_id}`)}
          data-testid="continue-learning"
          className="mt-6 flex w-full items-center justify-between rounded-3xl bg-[#0D9488] p-6 text-left text-white shadow-md transition-all hover:bg-[#0B7F74] active:translate-y-0.5">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-white/70">Continue Learning</div>
            <div className="mt-1 font-heading text-2xl font-extrabold">{cont.title}</div>
            <div className="font-body text-white/80">Level {cont.level_number}</div>
          </div>
          <Play size={40} className="shrink-0" />
        </motion.button>
      )}

      {/* Recommendations */}
      {overlay.recommendations?.length > 0 && (
        <div className="mt-6 rounded-3xl border-2 border-[#F59E0B]/40 bg-[#F59E0B]/5 p-5" data-testid="recommendations">
          <div className="font-heading text-lg font-bold text-[#B45309]">Recommended for You</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {overlay.recommendations.map((r) => (
              <button key={r.activity_id} onClick={() => navigate(`/activity/${r.activity_id}`)} className="rounded-2xl bg-white border border-[#E5DECF] px-4 py-2 font-bold text-[#1F2937] hover:border-[#F59E0B]">
                Practice {r.title} ({r.best_score}%)
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Curriculum map */}
      <h2 className="mt-10 font-heading text-2xl font-bold text-[#1F2937]">Your Learning Path</h2>
      <div className="mt-4 space-y-1">
        {overlay.levels.map((lvl, i) => {
          const tint = LEVEL_TINTS[i % LEVEL_TINTS.length];
          const clickable = lvl.status !== "locked" && lvl.status !== "coming_soon";
          return (
            <React.Fragment key={lvl.id}>
              <motion.button
                initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                onClick={() => clickable && navigate(`/level/${lvl.id}`)}
                data-testid={`level-card-${lvl.level_number}`}
                className={`flex w-full items-center gap-4 rounded-3xl border p-5 text-left transition-all ${clickable ? "border-[#E5DECF] bg-white hover:shadow-lg hover:-translate-y-0.5" : "border-[#E5DECF] bg-[#F2ECE1]/50 cursor-default"}`}>
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl font-heading text-xl font-extrabold text-white" style={{ background: tint, opacity: clickable ? 1 : 0.5 }}>{lvl.level_number}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-lg font-bold text-[#1F2937]">{lvl.title}</span>
                    <StatusBadge status={lvl.status} />
                    {lvl.status === "completed" && <Award size={16} color="#F59E0B" />}
                  </div>
                  <p className="mt-0.5 font-body text-sm text-[#6B7280] line-clamp-1">{lvl.description}</p>
                  {!lvl.coming_soon && (
                    <div className="mt-2 flex items-center gap-3">
                      <div className="h-2.5 w-40 max-w-[45vw] overflow-hidden rounded-full bg-[#F2ECE1]">
                        <div className="h-full rounded-full" style={{ width: `${lvl.progress_percentage ?? lvl.completion_percentage}%`, background: tint }} />
                      </div>
                      <span className="text-xs font-bold text-[#6B7280]">{lvl.progress_percentage ?? lvl.completion_percentage}% · {lvl.activities_attempted ?? lvl.activities_completed}/{lvl.activities_total} done</span>
                    </div>
                  )}
                </div>
              </motion.button>
              {i < overlay.levels.length - 1 && <div className="flex justify-center py-0.5"><ChevronDown size={18} className="text-[#94A3B8]" /></div>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
